import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { Hotel, Ota } from 'frontend-utils/models';
import ParityTooltipsTooltipPosTableLoss from 'lumen/components/parity/tooltips/tooltip-pos-table-loss';
import parityClass from 'lumen/helpers/parity-class';
import type { Period } from 'lumen/models';
import type { PeriodParity } from 'lumen/models/period';
import { filterMajorOTAs, filterParities } from 'lumen/utils/parities-filter';
import {
  type BaseRate,
  formatBaseRate,
  formatParity,
  type FormattedParity,
  sortParities,
} from 'lumen/utils/parity-utils';

export interface TdPosLowestRateOverOtasColumn {
  availableOTAs: Ota[];
  searchMajorOtas: boolean;
}

interface ParityTablesCellsTdPosLowestRateOverOtasSignature {
  Args: {
    cellData: Period;
    column: TdPosLowestRateOverOtasColumn;
  };
}

export default class ParityTablesCellsTdPosLowestRateOverOtas extends Component<ParityTablesCellsTdPosLowestRateOverOtasSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service declare rates: Services['rates'];

  get baseRates(): BaseRate[] {
    const { cellData, column } = this.args;

    const otaIds = column.availableOTAs?.map((ota) => ota.id);

    const baseRates = filterMajorOTAs(
      otaIds,
      // @ts-expect-error: Argument of type 'ParityDict | null' is not assignable to parameter of type 'ParityDict'.
      cellData.paritiesPOSDict,
      (parity) => parity.is_baserate,
    );

    return baseRates.map((baseRate) =>
      formatBaseRate(this.ota, this.rates, this.hotel, baseRate),
    );
  }

  get formattedChannels(): FormattedParity[] {
    const res: FormattedParity[] = [];

    if (!this.period.paritiesPOSDict) {
      return res;
    }

    const parities = filterParities(
      this.period.paritiesPOSDict,
      this.args.column.searchMajorOtas,
      this.args.column.availableOTAs,
      (parity) => parity.value !== 0 && parity.position_to_baserate < 0,
    );

    return parities
      .sort(sortParities)
      .slice(0, 15)
      .map((parity) => formatParity(this.ota, this.rates, this.hotel, parity));
  }

  get hotel(): Hotel | null {
    return this.hotelAndMarketService.hotel;
  }

  get isToday(): boolean {
    return this.period.day.isToday;
  }

  get lowestOTARateObject(): PeriodParity | null {
    let lowestOTARateObject: PeriodParity | null = null;

    if (this.period.paritiesPOSDict) {
      let lowestOTARate: number | null = null;
      // used to check if the ota is in OTAsParity
      const parities = filterParities(
        this.period.paritiesPOSDict,
        this.args.column.searchMajorOtas,
        this.args.column.availableOTAs,
        (parity) => parity.value !== 0 && parity.position_to_baserate < 0,
      );

      // Loop over parities to see who is the lowest
      parities.forEach((parity) => {
        if (!lowestOTARate || parity.value < lowestOTARate) {
          lowestOTARate = parity.value;
          lowestOTARateObject = parity;
        }
      });
    }

    return lowestOTARateObject;
  }

  get lowestOTARateValue(): string {
    if (this.lowestOTARateObject) {
      return `${this.rates.formatRateObject(this.lowestOTARateObject, false)} ${
        this.lowestOTARateObject?.currency
      }`;
    }

    return this.intl.t('general.missing');
  }

  get period(): Period {
    return this.args.cellData;
  }

  <template>
    <div class="truncate tabular-nums {{if this.isToday 'td-today'}}">
      <span
        {{! @glint-expect-error: Argument of type 'PeriodParity | null' is not assignable to parameter of type 'PeriodParity | undefined'. }}
        class={{parityClass this.lowestOTARateObject}}
        role="tooltip"
        title={{this.lowestOTARateValue}}
      >
        {{this.lowestOTARateValue}}
        {{#if this.lowestOTARateObject}}
          <ParityTooltipsTooltipPosTableLoss
            @baseRates={{this.baseRates}}
            @formattedChannels={{this.formattedChannels}}
          />
        {{/if}}
      </span>
    </div>
  </template>
}
